import React, { useState } from 'react';
import { useFormularioAnalytics, FieldAnalytics } from '@/hooks/useFormularioAnalytics';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, Legend } from 'recharts';
import { Activity, BarChart3, CheckCircle, Clock, TrendingUp, XCircle } from 'lucide-react';

interface FormularioAnalyticsDashboardProps {
  formularioId: string;
  formularioTitulo: string;
  onClose: () => void;
}

const COLORS = ['hsl(var(--chart-1))', 'hsl(var(--chart-2))', 'hsl(var(--chart-3))', 'hsl(var(--chart-4))', 'hsl(var(--chart-5))'];

export function FormularioAnalyticsDashboard({
  formularioId,
  formularioTitulo,
  onClose,
}: FormularioAnalyticsDashboardProps) {
  const { metricsOverview, fieldAnalytics, timelineData, isLoading } = useFormularioAnalytics(formularioId);
  const [campoSelecionado, setCampoSelecionado] = useState<string>('');

  const campoAnalytics = fieldAnalytics.find(f => f.campo_id === campoSelecionado);

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
        <div className="w-full max-w-7xl h-[90vh] bg-background shadow-lg overflow-y-auto rounded-lg m-4 p-8">
          <p className="text-center text-muted-foreground">Carregando analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center">
      <div className="w-full max-w-7xl h-[90vh] bg-background shadow-lg overflow-y-auto rounded-lg m-4">
        <div className="container max-w-6xl py-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-3xl font-bold">Dashboard de Analytics</h2>
              <p className="text-muted-foreground mt-1">{formularioTitulo}</p>
            </div>
            <Button variant="ghost" onClick={onClose}>✕</Button>
          </div>

          {/* Overview Cards */}
          {metricsOverview && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Respostas</CardTitle>
                  <Activity className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{metricsOverview.totalRespostas}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {metricsOverview.concluidas} concluídas
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Taxa de Conclusão</CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{metricsOverview.taxaConclusao.toFixed(1)}%</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {metricsOverview.concluidas} de {metricsOverview.totalRespostas}
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Tempo Médio</CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{metricsOverview.tempoMedioMinutos} min</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    Tempo de preenchimento
                  </p>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Validações</CardTitle>
                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{metricsOverview.validadas}</div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {metricsOverview.rejeitadas} rejeitadas
                  </p>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Timeline */}
          {timelineData.length > 0 && (
            <Card className="mb-8">
              <CardHeader>
                <CardTitle>Evolução de Respostas</CardTitle>
                <CardDescription>Número de respostas ao longo do tempo</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={250}>
                  <LineChart data={timelineData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="data" />
                    <YAxis />
                    <Tooltip />
                    <Line type="monotone" dataKey="count" stroke="hsl(var(--primary))" strokeWidth={2} name="Respostas" />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Análise por Campo */}
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Análise por Campo</CardTitle>
              <CardDescription>Selecione um campo para visualizar suas estatísticas</CardDescription>
              
              <Select value={campoSelecionado} onValueChange={setCampoSelecionado}>
                <SelectTrigger className="w-full max-w-md mt-4">
                  <SelectValue placeholder="Selecione um campo" />
                </SelectTrigger>
                <SelectContent>
                  {fieldAnalytics.map(field => (
                    <SelectItem key={field.campo_id} value={field.campo_id}>
                      {field.label} ({field.tipo_campo})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </CardHeader>

            {campoAnalytics && (
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">Total de Respostas</div>
                      <div className="text-2xl font-bold mt-2">{campoAnalytics.total_respostas}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">Taxa de Preenchimento</div>
                      <div className="text-2xl font-bold mt-2">{campoAnalytics.taxa_preenchimento.toFixed(1)}%</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6">
                      <div className="text-sm text-muted-foreground">Tipo do Campo</div>
                      <div className="text-2xl font-bold mt-2 capitalize">{campoAnalytics.tipo_campo}</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Visualizações específicas por tipo */}
                {renderFieldVisualization(campoAnalytics)}
              </CardContent>
            )}
          </Card>

          {/* Resumo de Campos */}
          <Card>
            <CardHeader>
              <CardTitle>Resumo de Todos os Campos</CardTitle>
              <CardDescription>Taxa de preenchimento por campo</CardDescription>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={fieldAnalytics}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" angle={-45} textAnchor="end" height={100} />
                  <YAxis />
                  <Tooltip />
                  <Bar dataKey="taxa_preenchimento" fill="hsl(var(--primary))" name="Taxa de Preenchimento (%)" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

function renderFieldVisualization(analytics: FieldAnalytics) {
  const { tipo_campo, dados_agregados } = analytics;

  // Campos numéricos
  if (tipo_campo === 'numero' && dados_agregados.media !== undefined) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Estatísticas</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Média:</span>
              <span className="font-bold">{dados_agregados.media}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Mediana:</span>
              <span className="font-bold">{dados_agregados.mediana}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Mínimo:</span>
              <span className="font-bold">{dados_agregados.min}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Máximo:</span>
              <span className="font-bold">{dados_agregados.max}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Desvio Padrão:</span>
              <span className="font-bold">{dados_agregados.desvio_padrao}</span>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Campos de seleção (radio, select, checkbox)
  if ((tipo_campo === 'radio' || tipo_campo === 'select' || tipo_campo === 'checkbox') && dados_agregados.opcoes) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={dados_agregados.opcoes}
              dataKey="count"
              nameKey="opcao"
              cx="50%"
              cy="50%"
              outerRadius={100}
              label={(entry) => `${entry.opcao}: ${entry.count}`}
            >
              {dados_agregados.opcoes.map((_, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip />
          </PieChart>
        </ResponsiveContainer>

        <div className="space-y-2">
          <h4 className="font-semibold mb-4">Distribuição</h4>
          {dados_agregados.opcoes.map((opcao, index) => (
            <div key={index} className="flex items-center justify-between p-2 border rounded">
              <span>{opcao.opcao}</span>
              <div className="flex items-center gap-2">
                <span className="font-bold">{opcao.count}</span>
                <span className="text-sm text-muted-foreground">({opcao.percentual.toFixed(1)}%)</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Campos de escala
  if (tipo_campo === 'escala' && dados_agregados.distribuicao_escala) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Média da Escala</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-4xl font-bold text-center">{dados_agregados.media_escala}</div>
          </CardContent>
        </Card>

        <ResponsiveContainer width="100%" height={250}>
          <BarChart data={dados_agregados.distribuicao_escala}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="valor" />
            <YAxis />
            <Tooltip />
            <Bar dataKey="count" fill="hsl(var(--primary))" name="Quantidade" />
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // Campos de texto
  if ((tipo_campo === 'texto' || tipo_campo === 'textarea') && dados_agregados.respostas_unicas !== undefined) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Estatísticas de Texto</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Respostas Únicas:</span>
              <span className="font-bold">{dados_agregados.respostas_unicas}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Comprimento Médio:</span>
              <span className="font-bold">{dados_agregados.comprimento_medio} caracteres</span>
            </div>
          </CardContent>
        </Card>

        {dados_agregados.palavras_frequentes && dados_agregados.palavras_frequentes.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Palavras Mais Frequentes</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {dados_agregados.palavras_frequentes.map((palavra, index) => (
                  <div key={index} className="flex items-center justify-between p-2 border rounded">
                    <span className="font-medium">{palavra.palavra}</span>
                    <span className="text-sm text-muted-foreground">{palavra.frequencia}x</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    );
  }

  // Campos de data
  if (tipo_campo === 'data' && dados_agregados.distribuicao_temporal) {
    return (
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={dados_agregados.distribuicao_temporal}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="periodo" />
          <YAxis />
          <Tooltip />
          <Bar dataKey="count" fill="hsl(var(--primary))" name="Respostas" />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  return (
    <p className="text-center text-muted-foreground py-8">
      Visualização não disponível para este tipo de campo
    </p>
  );
}
